import { Button as BaseButton } from "@base-ui/react/button";
import { Loader2 } from "lucide-react";
import type { ComponentPropsWithoutRef, FunctionComponent, ReactNode, Ref } from "react";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";

/** antd `type` (primary / default / dashed / text / link) — renamed so `type` stays the native attribute. */
export type ButtonVariant = "primary" | "default" | "dashed" | "text" | "link";
export type ButtonSize = "small" | "middle" | "large";

export interface ButtonProps extends Omit<ComponentPropsWithoutRef<"button">, "type"> {
  variant?: ButtonVariant;
  danger?: boolean;
  /** Replace the icon with a spinner and block interaction. */
  loading?: boolean;
  /** Leading icon; with no children the button becomes a square icon button. */
  icon?: ReactNode;
  size?: ButtonSize;
  shape?: "default" | "circle";
  /** Full width. */
  block?: boolean;
  /** Native button type (antd `htmlType`). Defaults to "button", not the browser's "submit". */
  type?: "button" | "submit" | "reset";
  /** Render an `<a>` instead of a `<button>`. */
  href?: string;
  target?: string;
  rel?: string;
  ref?: Ref<HTMLElement>;
}

const SIZE: Record<ButtonSize, string> = {
  small: "h-6 rounded px-[7px] text-sm",
  middle: "h-8 rounded-md px-[15px] text-sm",
  large: "h-10 rounded-lg px-[15px] text-base",
};

const ICON_ONLY_SIZE: Record<ButtonSize, string> = {
  small: "w-6 px-0",
  middle: "w-8 px-0",
  large: "w-10 px-0",
};

// Primary = bg-noa-primary (the primary brand color) with text-noa-on-primary content; default/dashed
// paint the base surface and brighten on hover. Hovers use the palette's -hover tokens.
const VARIANT: Record<ButtonVariant, { normal: string; danger: string }> = {
  primary: {
    normal: "border-transparent bg-noa-primary text-noa-on-primary hover:bg-noa-primary-hover",
    danger: "border-transparent bg-noa-danger text-noa-on-danger hover:bg-noa-danger-hover",
  },
  default: {
    normal: "border-noa bg-noa text-noa hover:bg-noa-hover hover:text-noa-strong",
    danger: "border-noa-danger-strong bg-noa text-noa-danger hover:bg-noa-danger-subtle",
  },
  dashed: {
    normal: "border-dashed border-noa bg-noa text-noa hover:bg-noa-hover hover:text-noa-strong",
    danger: "border-dashed border-noa-danger-strong bg-noa text-noa-danger hover:bg-noa-danger-subtle",
  },
  text: {
    normal: "border-transparent bg-transparent text-noa hover:bg-noa-hover",
    danger: "border-transparent bg-transparent text-noa-danger hover:bg-noa-danger-subtle",
  },
  link: {
    normal: "border-transparent bg-transparent text-noa-link hover:text-noa-link-hover",
    danger: "border-transparent bg-transparent text-noa-danger hover:text-noa-danger-hover",
  },
};

const DISABLED: Record<ButtonVariant, string> = {
  primary: "data-[disabled]:border-noa data-[disabled]:bg-noa-hover data-[disabled]:text-noa-disabled",
  default: "data-[disabled]:border-noa data-[disabled]:bg-noa-hover data-[disabled]:text-noa-disabled",
  dashed: "data-[disabled]:border-noa data-[disabled]:bg-noa-hover data-[disabled]:text-noa-disabled",
  text: "data-[disabled]:bg-transparent data-[disabled]:text-noa-disabled",
  link: "data-[disabled]:bg-transparent data-[disabled]:text-noa-disabled",
};

/** antd-compatible button (Base UI `Button`); `variant` replaces antd's `type`, `type` replaces `htmlType`. */
export const Button: FunctionComponent<ButtonProps> = ({
  variant = "default",
  danger,
  loading,
  icon,
  size = "middle",
  shape = "default",
  block,
  type = "button",
  href,
  target,
  rel,
  disabled,
  className,
  children,
  ref,
  ...props
}) => {
  const isDisabled = disabled || loading;
  const iconOnly = children === undefined || children === null || children === false;
  const leading = loading ? <Loader2 className="animate-spin" size={size === "small" ? 12 : 14} /> : icon;

  const classes = cn(
    "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 border font-normal whitespace-nowrap transition-colors select-none",
    FOCUS_RING,
    "data-[disabled]:cursor-not-allowed [&_svg]:shrink-0",
    SIZE[size],
    iconOnly && (!!leading || shape === "circle") && ICON_ONLY_SIZE[size],
    shape === "circle" && "rounded-full",
    block && "w-full",
    VARIANT[variant][danger ? "danger" : "normal"],
    DISABLED[variant],
    loading && "cursor-default",
    className
  );

  const content = (
    <>
      {leading}
      {!iconOnly && children}
    </>
  );

  if (href) {
    return (
      <a
        ref={ref as Ref<HTMLAnchorElement>}
        href={isDisabled ? undefined : href}
        target={target}
        rel={rel}
        aria-disabled={isDisabled || undefined}
        data-disabled={isDisabled ? "" : undefined}
        tabIndex={isDisabled ? -1 : undefined}
        className={classes}
        {...(props as ComponentPropsWithoutRef<"a">)}
        onClick={(event) => {
          if (isDisabled) return event.preventDefault();
          (props as ComponentPropsWithoutRef<"a">).onClick?.(event);
        }}
      >
        {content}
      </a>
    );
  }

  return (
    <BaseButton
      ref={ref}
      type={type}
      disabled={isDisabled}
      // A loading button stays focusable (Base UI still blocks its clicks), so focus isn't dropped mid-action.
      focusableWhenDisabled={loading && !disabled}
      aria-busy={loading || undefined}
      className={classes}
      {...props}
    >
      {content}
    </BaseButton>
  );
};
